import React, { useEffect, useState, useCallback } from "react";
import {
  LifeBuoy, Shield, Scale, Radio, RefreshCw, Printer, Send, Lock, Unlock,
  ChevronDown, Paperclip, Bot, User, MailCheck, CircleAlert, Inbox, Ban, ShieldCheck, Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { fmtFecha } from "@/lib/format";
import { toast } from "sonner";
import "./soporte-print.css";

const BUZONES = [
  { key: "support", label: "Support", icon: LifeBuoy, color: "#4ECDC4" },
  { key: "privacy", label: "Privacy", icon: Shield, color: "#D4AF37" },
  { key: "legal", label: "Legal", icon: Scale, color: "#A78BFA" },
];

const ESTADOS = {
  nuevo: { label: "Sin atender", dot: "#3B82F6" },
  auto_respondido: { label: "Acuse enviado", dot: "#F59E0B" },
  respondido_ia: { label: "Resuelto por IA", dot: "#4ECDC4" },
  esperando_respuesta: { label: "Espera operador", dot: "#F97316" },
  respondido: { label: "Respondido", dot: "#22C55E" },
  cerrado: { label: "Cerrado", dot: "#52525b" },
  no_registrado: { label: "No registrado", dot: "#A1A1AA" },
  descartado: { label: "Descartado", dot: "#EF4444" },
};

const EstadoBadge = ({ estado }) => {
  const e = ESTADOS[estado] || { label: estado || "—", dot: "#71717A" };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-[#A1A1AA]" data-testid={`estado-${estado}`}>
      <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: e.dot }} />
      {e.label}
    </span>
  );
};

const RespuestaLinea = ({ r }) => {
  const esIA = r.tipo === "ia";
  return (
    <div className="ml-4 mt-2 rounded-lg bg-[#050505] border border-[#1F1F24] px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        {esIA
          ? <span className="inline-flex items-center gap-1 text-[11px] text-[#4ECDC4] uppercase tracking-wider"><Bot className="w-3.5 h-3.5" /> Respondió la IA</span>
          : <span className="inline-flex items-center gap-1 text-[11px] text-[#D4AF37] uppercase tracking-wider"><User className="w-3.5 h-3.5" /> Respondió el operador</span>}
        <span className="font-mono-ax text-[10px] text-[#52525b]">{fmtFecha(r.enviado_at)}</span>
      </div>
      <p className="text-[#D4D4D8] text-sm whitespace-pre-wrap">{r.cuerpo}</p>
    </div>
  );
};

const Soporte = () => {
  const [vista, setVista] = useState("bandeja"); // bandeja | cuarentenas
  const [buzon, setBuzon] = useState("support");
  const [estado, setEstado] = useState("");
  const [data, setData] = useState(null);
  const [pend, setPend] = useState(false);
  const [abierto, setAbierto] = useState(null); // ticket expandido
  const [detalle, setDetalle] = useState({}); // { ticket: detailObj }
  const [cargandoDet, setCargandoDet] = useState(null);
  const [respuesta, setRespuesta] = useState({}); // { ticket: texto }
  const [accion, setAccion] = useState(false);
  const [revisando, setRevisando] = useState(false);
  const [printDoc, setPrintDoc] = useState(null);

  // Cuarentenas
  const [bloqueados, setBloqueados] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [nuevoBloqueo, setNuevoBloqueo] = useState({ remitente: "", motivo: "" });

  const cargarBandeja = useCallback(async () => {
    try {
      const url = `/comando/soporte?buzon=${buzon}&limit=100${estado ? `&estado=${estado}` : ""}`;
      const { data } = await api.get(url);
      setPend(!!data?.puente_pendiente);
      setData(data);
    } catch (e) { /* noop */ }
  }, [buzon, estado]);

  const cargarCuarentenas = useCallback(async () => {
    try {
      const [b, g] = await Promise.all([
        api.get("/comando/soporte/bloqueados"),
        api.get("/comando/guardian"),
      ]);
      setBloqueados(b.data);
      setGuardian(g.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    document.title = "Soporte · AXIORA";
  }, []);

  useEffect(() => {
    if (vista === "bandeja") {
      cargarBandeja();
      const t = setInterval(cargarBandeja, 60000);
      return () => clearInterval(t);
    }
    cargarCuarentenas();
    const t = setInterval(cargarCuarentenas, 60000);
    return () => clearInterval(t);
  }, [vista, cargarBandeja, cargarCuarentenas]);

  const toggleTicket = async (ticket, rowId) => {
    if (abierto === rowId) { setAbierto(null); return; }
    setAbierto(rowId);
    if (!detalle[ticket]) {
      setCargandoDet(rowId);
      try {
        const { data } = await api.get(`/comando/soporte/ticket/${ticket}`);
        if (!data?.puente_pendiente) setDetalle((d) => ({ ...d, [ticket]: data }));
        else toast.error("PUENTE PENDIENTE — no se pudo abrir el expediente");
      } catch (e) { toast.error("No se pudo abrir el expediente"); }
      finally { setCargandoDet(null); }
    }
  };

  const responder = async (ticket) => {
    const det = detalle[ticket];
    const cuerpo = (respuesta[ticket] || "").trim();
    if (!det || !cuerpo) return;
    const hist = det.historial || [];
    const ultimo = hist[hist.length - 1];
    if (!ultimo?.id) { toast.error("No hay mensaje al que responder"); return; }
    setAccion(true);
    try {
      const { data } = await api.post(`/comando/soporte/${ultimo.id}/responder`, { cuerpo });
      if (data?.puente_pendiente) { toast.error("PUENTE PENDIENTE — no se envió"); return; }
      toast.success("Respuesta enviada");
      setRespuesta((r) => ({ ...r, [ticket]: "" }));
      setDetalle((d) => { const c = { ...d }; delete c[ticket]; return c; });
      await toggleTicketRefresh(ticket);
      cargarBandeja();
    } catch (e) { toast.error("No se pudo enviar la respuesta"); }
    finally { setAccion(false); }
  };

  const toggleTicketRefresh = async (ticket) => {
    try {
      const { data } = await api.get(`/comando/soporte/ticket/${ticket}`);
      if (!data?.puente_pendiente) setDetalle((d) => ({ ...d, [ticket]: data }));
    } catch (e) { /* noop */ }
  };

  const cerrar = async (ticket) => {
    setAccion(true);
    try {
      const { data } = await api.post(`/comando/soporte/ticket/${ticket}/cerrar`);
      if (data?.puente_pendiente) { toast.error("PUENTE PENDIENTE"); return; }
      toast.success("Caso cerrado");
      setDetalle((d) => { const c = { ...d }; delete c[ticket]; return c; });
      cargarBandeja();
    } catch (e) { toast.error("No se pudo cerrar el caso"); }
    finally { setAccion(false); }
  };

  const revisarAhora = async () => {
    setRevisando(true);
    try {
      const { data } = await api.post("/comando/soporte/revisar-ahora");
      if (data?.puente_pendiente) toast.error("PUENTE PENDIENTE");
      else { toast.success("Buzones revisados"); cargarBandeja(); }
    } catch (e) { toast.error("No se pudo revisar los buzones"); }
    finally { setRevisando(false); }
  };

  const bloquearManual = async () => {
    if (!nuevoBloqueo.remitente.trim()) return;
    setAccion(true);
    try {
      const { data } = await api.post("/comando/soporte/bloquear", {
        remitente: nuevoBloqueo.remitente.trim(), motivo: nuevoBloqueo.motivo.trim() || "manual",
      });
      if (data?.puente_pendiente) { toast.error("PUENTE PENDIENTE"); return; }
      toast.success("Remitente bloqueado");
      setNuevoBloqueo({ remitente: "", motivo: "" });
      cargarCuarentenas();
    } catch (e) { toast.error("No se pudo bloquear"); }
    finally { setAccion(false); }
  };

  const desbloquear = async (remitente) => {
    setAccion(true);
    try {
      const { data } = await api.post("/comando/soporte/desbloquear", { remitente });
      if (data?.puente_pendiente) { toast.error("PUENTE PENDIENTE"); return; }
      toast.success("Remitente desbloqueado"); cargarCuarentenas();
    } catch (e) { toast.error("No se pudo desbloquear"); }
    finally { setAccion(false); }
  };

  const permitir = async (remitente) => {
    setAccion(true);
    try {
      const { data } = await api.post("/comando/soporte/permitir", { remitente });
      if (data?.puente_pendiente) { toast.error("PUENTE PENDIENTE"); return; }
      toast.success("Excepción permitida"); cargarCuarentenas();
    } catch (e) { toast.error("No se pudo permitir"); }
    finally { setAccion(false); }
  };

  const imprimir = (det) => {
    setPrintDoc(det);
    setTimeout(() => { window.print(); }, 120);
  };

  const mensajes = data?.mensajes || [];
  const pendientes = data?.pendientes ?? 0;

  return (
    <div className="p-6 pb-32">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide flex items-center gap-3">
            <LifeBuoy className="w-7 h-7 text-[#D4AF37]" /> Soporte
          </h2>
          <p className="text-xs text-[#71717A] mt-0.5">Bandeja unificada · expedientes legales · cuarentenas — todo vía puente</p>
        </div>
        <div className="flex items-center gap-3">
          {vista === "bandeja" && (
            <button data-testid="revisar-ahora-btn" onClick={revisarAhora} disabled={revisando}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#1F1F24] text-[#A1A1AA] text-sm hover:border-[#4ECDC4]/40 hover:text-[#4ECDC4] disabled:opacity-50">
              {revisando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />} Revisar buzones ahora
            </button>
          )}
        </div>
      </header>

      {/* Sub-navegación vista */}
      <div className="flex items-center gap-2 mb-5">
        <button data-testid="vista-bandeja" onClick={() => setVista("bandeja")}
          className={`px-4 py-2 rounded-lg text-sm border ${vista === "bandeja" ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#1F1F24] text-[#A1A1AA] hover:text-[#F5F5F7]"}`}>
          <Inbox className="w-4 h-4 inline mr-2" /> Bandeja
        </button>
        <button data-testid="vista-cuarentenas" onClick={() => setVista("cuarentenas")}
          className={`px-4 py-2 rounded-lg text-sm border ${vista === "cuarentenas" ? "border-[#FF6B5B]/50 bg-[#FF6B5B]/10 text-[#FF6B5B]" : "border-[#1F1F24] text-[#A1A1AA] hover:text-[#F5F5F7]"}`}>
          <Ban className="w-4 h-4 inline mr-2" /> Cuarentenas y Bloqueos
        </button>
      </div>

      {pend && (
        <div data-testid="soporte-pendiente" className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — los buzones no están disponibles ahora.
        </div>
      )}

      {vista === "bandeja" ? (
        <>
          {/* Pestañas de buzón */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {BUZONES.map((b) => {
              const Icon = b.icon; const activo = buzon === b.key;
              return (
                <button key={b.key} data-testid={`buzon-${b.key}`} onClick={() => { setBuzon(b.key); setAbierto(null); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${activo ? "bg-[#0A0A0A] text-[#F5F5F7]" : "border-[#1F1F24] text-[#71717A] hover:text-[#F5F5F7]"}`}
                  style={activo ? { borderColor: b.color } : {}}>
                  <Icon className="w-4 h-4" style={{ color: b.color }} /> {b.label}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2 text-sm">
              <span className="text-[#71717A]">Pendientes:</span>
              <span data-testid="soporte-pendientes-count" className="font-mono-ax text-[#F39C12] text-lg">{pendientes}</span>
            </div>
          </div>

          {/* Filtro por estado */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button data-testid="filtro-estado-todos" onClick={() => setEstado("")}
              className={`px-3 py-1.5 rounded-full text-xs border ${estado === "" ? "border-[#D4AF37]/50 text-[#D4AF37]" : "border-[#1F1F24] text-[#71717A] hover:text-[#F5F5F7]"}`}>
              Todos
            </button>
            {Object.entries(ESTADOS).map(([k, v]) => (
              <button key={k} data-testid={`filtro-estado-${k}`} onClick={() => setEstado(k)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${estado === k ? "text-[#F5F5F7]" : "border-[#1F1F24] text-[#71717A] hover:text-[#F5F5F7]"}`}
                style={estado === k ? { borderColor: v.dot } : {}}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: v.dot }} /> {v.label}
              </button>
            ))}
          </div>

          {/* Lista de tickets */}
          <div className="space-y-3">
            {mensajes.length ? mensajes.map((m) => {
              const rowId = m.id;
              const isOpen = abierto === rowId;
              const det = detalle[m.ticket];
              return (
                <div key={rowId} data-testid={`ticket-${m.ticket}`} className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
                  <button data-testid={`ticket-toggle-${m.ticket}`} onClick={(e) => {
                      toggleTicket(m.ticket, rowId);
                      const card = e.currentTarget.closest(`[data-testid="ticket-${m.ticket}"]`);
                      setTimeout(() => card?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 120);
                    }}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-white/[0.02] text-left">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-ax text-xs text-[#D4AF37]">{m.ticket}</span>
                        {m.tiene_adjuntos && (
                          <span title={(m.adjuntos || []).join(", ")} className="inline-flex items-center gap-1 text-[10px] text-[#F39C12]">
                            <Paperclip className="w-3 h-3" /> {(m.adjuntos || []).length}
                          </span>
                        )}
                      </div>
                      <p className="text-[#F5F5F7] text-sm truncate mt-0.5">{m.asunto || "(sin asunto)"}</p>
                      <p className="text-[#71717A] text-xs truncate">{m.remitente_nombre ? `${m.remitente_nombre} · ` : ""}{m.remitente}</p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <EstadoBadge estado={m.estado} />
                      <span className="font-mono-ax text-[10px] text-[#52525b] hidden md:inline">{fmtFecha(m.recibido_at)}</span>
                      <ChevronDown className={`w-4 h-4 text-[#71717A] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-[#1F1F24] pt-4">
                      {cargandoDet === rowId && (
                        <div className="flex items-center gap-2 text-[#71717A] text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Cargando expediente…</div>
                      )}
                      {det && (
                        <>
                          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                            <span className="text-xs text-[#71717A]">Expediente · {(det.historial || []).length} mensaje(s) {det.cerrado ? "· caso cerrado" : ""}</span>
                            <div className="flex items-center gap-2">
                              <button data-testid={`imprimir-${m.ticket}`} onClick={() => imprimir(det)}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#1F1F24] text-[#A1A1AA] hover:text-[#F5F5F7]">
                                <Printer className="w-3.5 h-3.5" /> Imprimir expediente
                              </button>
                              {!det.cerrado && (
                                <button data-testid={`cerrar-${m.ticket}`} onClick={() => cerrar(m.ticket)} disabled={accion}
                                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#1F1F24] text-[#A1A1AA] hover:text-[#FF6B5B] hover:border-[#FF6B5B]/40 disabled:opacity-50">
                                  <Lock className="w-3.5 h-3.5" /> Cerrar caso
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Línea de tiempo */}
                          <div className="space-y-4">
                            {(det.historial || []).map((h, i) => (
                              <div key={h.id || i} className="rounded-lg bg-[#050505] border border-[#1F1F24] p-4">
                                <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                                  <span className="text-[#F5F5F7] text-sm font-medium">{h.asunto || "(sin asunto)"}</span>
                                  <span className="font-mono-ax text-[10px] text-[#52525b]">{fmtFecha(h.recibido_at)}</span>
                                </div>
                                <p className="text-[#71717A] text-xs mb-2">{h.remitente_nombre ? `${h.remitente_nombre} · ` : ""}{h.remitente}</p>
                                <p className="text-[#D4D4D8] text-sm whitespace-pre-wrap">{h.cuerpo}</p>
                                {(h.message_id || h.fecha_original || h.ia_clasificacion || (h.adjuntos || []).length > 0) && (
                                  <div className="mt-2 font-mono-ax text-[10px] text-[#52525b] space-y-0.5 border-t border-[#1F1F24] pt-2">
                                    {h.fecha_original && <div>fecha original: {h.fecha_original}</div>}
                                    {h.message_id && <div className="break-all">message-id: {h.message_id}</div>}
                                    {h.ia_clasificacion && <div>clasificación IA: {h.ia_clasificacion}</div>}
                                    {(h.adjuntos || []).length > 0 && <div>adjuntos declarados: {(h.adjuntos || []).join(", ")}</div>}
                                    {h.acuse_at && <div>acuse automático: {fmtFecha(h.acuse_at)}</div>}
                                  </div>
                                )}
                                {(h.respuestas || []).map((r, j) => <RespuestaLinea key={j} r={r} />)}
                              </div>
                            ))}
                          </div>

                          {/* Responder */}
                          {!det.cerrado && (
                            <div className="mt-4">
                              <Textarea data-testid={`respuesta-input-${m.ticket}`} value={respuesta[m.ticket] || ""}
                                onChange={(e) => setRespuesta((r) => ({ ...r, [m.ticket]: e.target.value }))}
                                placeholder="Escribe tu respuesta oficial… (sale desde la casilla del buzón con el ticket en el asunto)"
                                className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] min-h-[90px]" />
                              <div className="flex justify-end mt-2">
                                <button data-testid={`responder-btn-${m.ticket}`} onClick={() => responder(m.ticket)} disabled={accion || !(respuesta[m.ticket] || "").trim()}
                                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A0A0A] font-semibold text-sm hover:opacity-90 disabled:opacity-40">
                                  {accion ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Responder
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] px-5 py-10 text-center text-[#52525b] text-sm">
                {pend ? "PUENTE PENDIENTE" : "No hay mensajes en este buzón con el filtro actual."}
              </div>
            )}
          </div>
        </>
      ) : (
        <Cuarentenas
          bloqueados={bloqueados} guardian={guardian} accion={accion}
          nuevoBloqueo={nuevoBloqueo} setNuevoBloqueo={setNuevoBloqueo}
          onBloquear={bloquearManual} onDesbloquear={desbloquear} onPermitir={permitir}
        />
      )}

      {/* Documento imprimible (oculto salvo al imprimir) */}
      {printDoc && <PrintDoc det={printDoc} />}
    </div>
  );
};

const Cuarentenas = ({ bloqueados, guardian, accion, nuevoBloqueo, setNuevoBloqueo, onBloquear, onDesbloquear, onPermitir }) => {
  const lista = bloqueados?.bloqueados || [];
  const g = guardian || {};
  const retenidos = g.bloqueos_retenidos || [];
  const eventos = (g.acciones || []).filter((a) => ["soporte_flood", "soporte_sospechoso", "soporte_salida_bloqueada"].includes(a.tipo));

  return (
    <div className="space-y-6">
      {/* Bloqueo manual */}
      <section className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5">
        <div className="flex items-center gap-2 mb-4"><Ban className="w-5 h-5 text-[#FF6B5B]" /><h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider">Bloquear remitente manualmente</h3></div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] text-[#71717A] uppercase tracking-wider">Remitente (email)</label>
            <Input data-testid="bloqueo-remitente-input" value={nuevoBloqueo.remitente}
              onChange={(e) => setNuevoBloqueo((s) => ({ ...s, remitente: e.target.value }))}
              placeholder="correo@dominio.com" className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] mt-1" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] text-[#71717A] uppercase tracking-wider">Motivo</label>
            <Input data-testid="bloqueo-motivo-input" value={nuevoBloqueo.motivo}
              onChange={(e) => setNuevoBloqueo((s) => ({ ...s, motivo: e.target.value }))}
              placeholder="spam / abuso" className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] mt-1" />
          </div>
          <button data-testid="bloqueo-manual-btn" onClick={onBloquear} disabled={accion || !nuevoBloqueo.remitente.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B5B]/15 text-[#FF6B5B] border border-[#FF6B5B]/40 text-sm hover:bg-[#FF6B5B]/25 disabled:opacity-40">
            <Lock className="w-4 h-4" /> Bloquear
          </button>
        </div>
      </section>

      {/* Remitentes bloqueados del buzón */}
      <section>
        <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider mb-3">Remitentes bloqueados del buzón ({lista.length})</h3>
        <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
          {lista.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#050505]"><tr className="text-[10px] uppercase tracking-wider text-[#71717A]">
                  <th className="text-left px-4 py-2.5">Remitente</th><th className="text-left px-4 py-2.5">Motivo</th>
                  <th className="text-left px-4 py-2.5">Origen</th><th className="text-left px-4 py-2.5">Desde</th>
                  <th className="text-left px-4 py-2.5">Hasta</th><th className="text-right px-4 py-2.5">Acciones</th>
                </tr></thead>
                <tbody>
                  {lista.map((b, i) => (
                    <tr key={i} data-testid={`bloqueado-${i}`} className="border-t border-[#1F1F24]">
                      <td className="px-4 py-3 font-mono-ax text-xs text-[#F5F5F7]">{b.remitente}</td>
                      <td className="px-4 py-3 text-[#A1A1AA] text-xs">{b.motivo || "—"}</td>
                      <td className="px-4 py-3 text-xs">{b.auto ? <span className="text-[#F39C12]">automático</span> : <span className="text-[#A78BFA]">manual</span>}</td>
                      <td className="px-4 py-3 font-mono-ax text-[10px] text-[#71717A]">{fmtFecha(b.bloqueado_at)}</td>
                      <td className="px-4 py-3 font-mono-ax text-[10px] text-[#71717A]">{b.hasta ? fmtFecha(b.hasta) : "permanente"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button data-testid={`desbloquear-${i}`} onClick={() => onDesbloquear(b.remitente)} disabled={accion}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-md bg-[#4ECDC4]/15 text-[#4ECDC4] border border-[#4ECDC4]/40 hover:bg-[#4ECDC4]/25 disabled:opacity-40"><Unlock className="w-3 h-3" /> Desbloquear</button>
                          <button data-testid={`permitir-${i}`} onClick={() => onPermitir(b.remitente)} disabled={accion}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-md border border-[#1F1F24] text-[#A1A1AA] hover:text-[#F5F5F7]"><ShieldCheck className="w-3 h-3" /> Permitir</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-8 text-center text-[#52525b] text-sm">No hay remitentes bloqueados.</div>
          )}
        </div>
      </section>

      {/* Usuarios en cuarentena (Guardián) */}
      <section>
        <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider mb-3">Usuarios de la app en cuarentena — Guardián ({retenidos.length})</h3>
        {retenidos.length ? (
          <div className="space-y-2">
            {retenidos.map((b) => (
              <div key={b.user_id} data-testid={`cuarentena-${b.user_id}`} className="rounded-lg bg-[#FF6B5B]/8 border border-[#FF6B5B]/25 px-4 py-3">
                <p className="text-[#F5F5F7] text-sm font-mono-ax">{b.user_id}</p>
                <p className="text-[#A1A1AA] text-xs">{b.motivo || "—"}</p>
                <p className="text-[#52525b] text-[10px] mt-0.5">{fmtFecha(b.desde)} → {fmtFecha(b.hasta)} · renovaciones: {b.renovaciones_guardian ?? 0}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-[#52525b] text-sm">Sin usuarios retenidos por el Guardián.</p>}
      </section>

      {/* Eventos de abuso de soporte */}
      <section>
        <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider mb-3">Eventos de abuso de soporte ({eventos.length})</h3>
        {eventos.length ? (
          <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#050505]"><tr className="text-[10px] uppercase tracking-wider text-[#71717A]">
                <th className="text-left px-4 py-2.5">Tipo</th><th className="text-left px-4 py-2.5">Detalle</th><th className="text-left px-4 py-2.5">Cuándo</th>
              </tr></thead>
              <tbody>
                {eventos.map((a, i) => (
                  <tr key={i} data-testid={`evento-abuso-${i}`} className="border-t border-[#1F1F24]">
                    <td className="px-4 py-3 text-[#F39C12] text-xs uppercase tracking-wider">{a.tipo.replace("soporte_", "")}</td>
                    <td className="px-4 py-3 text-[#D4D4D8] text-xs">{a.detalle}</td>
                    <td className="px-4 py-3 font-mono-ax text-[10px] text-[#71717A]">{fmtFecha(a.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-[#52525b] text-sm">Sin eventos de flood ni intentos de manipulación de la IA registrados.</p>}
      </section>
    </div>
  );
};

const PrintDoc = ({ det }) => {
  const hist = det.historial || [];
  const total = hist.length;
  return (
    <div className="ax-print-doc" data-testid="print-doc">
      <h1>EXPEDIENTE DE COMUNICACIONES — {det.ticket}</h1>
      <p className="ax-print-empresa">{det.empresa || "Sentinel Black Unit, Inc. — Axiora"}</p>
      <p className="ax-print-meta">Documento generado el {fmtFecha(det.generado_at)}</p>
      <div className="ax-print-caso">
        <div><b>Buzón:</b> {det.buzon}</div>
        <div><b>Remitente:</b> {det.remitente}</div>
        <div><b>Estado final:</b> {det.cerrado ? "Caso cerrado" : "Abierto"}</div>
        <div><b>Total de mensajes:</b> {total}</div>
      </div>
      {hist.map((h, i) => (
        <div className="ax-print-evento" key={h.id || i}>
          <div><b>Evento {i + 1} de {total}</b> — {h.asunto || "(sin asunto)"}</div>
          <div className="ax-print-forense">
            recibido: {fmtFecha(h.recibido_at)}{"\n"}
            fecha original del correo: {h.fecha_original || "—"}{"\n"}
            message-id: {h.message_id || "—"}{"\n"}
            clasificación IA: {h.ia_clasificacion || "—"}{"\n"}
            adjuntos declarados: {(h.adjuntos || []).length ? (h.adjuntos || []).join(", ") : "ninguno"}
            {h.acuse_at ? `\nacuse automático: ${fmtFecha(h.acuse_at)}` : ""}
          </div>
          <div><b>De:</b> {h.remitente_nombre ? `${h.remitente_nombre} · ` : ""}{h.remitente}</div>
          <div className="ax-print-cuerpo">{h.cuerpo}</div>
          {(h.respuestas || []).map((r, j) => (
            <div className="ax-print-resp" key={j}>
              <div className="ax-print-quien">{r.tipo === "ia" ? "Respondió la IA" : "Respondió el operador"} — {fmtFecha(r.enviado_at)}</div>
              <div className="ax-print-cuerpo">{r.cuerpo}</div>
            </div>
          ))}
        </div>
      ))}
      <div className="ax-print-footer">
        Documento generado desde los registros originales del sistema Axiora el {fmtFecha(det.generado_at)}. Ningún contenido fue editado ni omitido.
      </div>
    </div>
  );
};

export default Soporte;
